import { journeyToken, JourneyStages } from '@cognitoforms/api/services/journey-context';
import type { StageStart } from '@cognitoforms/api/services/journey-context';
import { allPlans } from '@cognitoforms/utils/variant-plans';
import { readPricingExperimentBucketing } from '@cognitoforms/utils/pricing-experiment';

/**
 * What each value of the signup page's `source` parameter means as an entry point. It is a query parameter,
 * so the map is what keeps a reporting dimension from becoming unbounded free text. Most surfaces name the
 * entry point directly; a template signup names the kind of template it came from instead.
 */
const signupOrigins: Record<string, string> = {
	HomePageCTA: 'HomePageCTA',
	PricingPageCTA: 'PricingPageCTA',
	TemplateGalleryCTA: 'TemplateGalleryCTA',
	NavBar: 'NavBar',
	JotformHandoff: 'JotformHandoff',
	templates: 'TemplateGalleryCTA',
	'shared-template': 'SharedTemplateCTA'
};

/**
 * What arriving at the signup page means, from the parameters the arrival carries. `Variant` is what the
 * person was trying to do and stays a small closed set; where they came from goes to `entryPoint`. Pass
 * `isInvitation` for an arrival that came from an invitation link, which reaches the login page rather than
 * the signup page.
 */
export function resolveSignupUserStart(source?: unknown, returnUrl?: unknown, isInvitation = false): StageStart {
	if (isInvitation)
		return { stage: JourneyStages.SignupUser, variant: 'Invite' };

	if (typeof returnUrl === 'string' && returnUrl.toLowerCase().includes('api-connection'))
		return { stage: JourneyStages.SignupUser, variant: 'IntegrationConnect' };

	const origin = typeof source === 'string' && Object.prototype.hasOwnProperty.call(signupOrigins, source) ? signupOrigins[source] : undefined;

	if (origin === 'JotformHandoff')
		return { stage: JourneyStages.SignupUser, variant: 'Jotform', entryPoint: origin };

	const start: StageStart = { stage: JourneyStages.SignupUser, variant: 'Website' };
	if (origin)
		start.entryPoint = origin;
	return start;
}

/**
 * Billing intervals formatted to be reported by the customer journey reporting
 */
const reportedIntervals: Record<string, string> = {
	monthly: 'Monthly',
	annual: 'Annual',
	legacy: 'Legacy'
};

const MAX_CAMPAIGN_KEY_LENGTH = 32;

const DIMENSION_NO_VALUE = 'None';

/** The offer a signup carries, which rides on the `returnUrl` the plan it chose sent it to signup with. */
function signupOffer(returnUrl?: unknown): URLSearchParams {
	if (typeof returnUrl !== 'string')
		return new URLSearchParams();

	const query = returnUrl.indexOf('?');
	if (query < 0)
		return new URLSearchParams();

	return new URLSearchParams(returnUrl.slice(query + 1));
}

function targetPlan(offer: URLSearchParams): string {
	const plan = offer.get('plan')?.toLowerCase();
	return allPlans.find(name => name.toLowerCase() === plan) ?? DIMENSION_NO_VALUE;
}

/**
 * The billing term a dimension reports, or `None` when there is no term to report - a free plan has no
 * cadence, so reporting the UI's default for one would invent a term the organization never had.
 */
export function reportedInterval(interval: string | null | undefined): string {
	const key = interval?.toLowerCase();
	if (key && Object.prototype.hasOwnProperty.call(reportedIntervals, key))
		return reportedIntervals[key];

	return DIMENSION_NO_VALUE;
}

/**
 * The plan, cadence, and pricing-experiment dimensions common to every `ConvertToPaid`/`ChangePlan` Start
 * and the CTA click that opens either stage. `currentInterval` is reported as-is through
 * {@link reportedInterval}, so pass `null` where the org has no cadence to report (never paid, or on a
 * trial) rather than inventing one.
 */
export function resolveCurrentPlanDimensions(currentPlanName: string | null, currentInterval: string | null, arm: string | null | undefined, experiment: string | null | undefined): Record<string, string> {
	return {
		'data-cf-current-plan': currentPlanName || DIMENSION_NO_VALUE,
		'data-cf-current-interval': reportedInterval(currentInterval),
		'data-cf-arm': arm ?? DIMENSION_NO_VALUE,
		'data-cf-experiment': experiment ?? DIMENSION_NO_VALUE
	};
}

type ContactSalesOrganization = {
	readonly Plan?: { readonly IsPaidPlan?: boolean; readonly Product?: { readonly Name?: string } | null } | null;
	readonly BillingMode?: string | null;
	readonly IsTrial?: boolean;
	readonly Role?: string | null;
};

export function resolveContactSalesDimensions(organization: ContactSalesOrganization | null | undefined): Record<string, string> {
	const isTrial = organization?.IsTrial === true;

	let currentInterval: string | null = null;
	if (organization?.Plan?.IsPaidPlan && !isTrial)
		currentInterval = organization.BillingMode ?? null;

	return {
		'data-cf-current-plan': organization?.Plan?.Product?.Name || DIMENSION_NO_VALUE,
		'data-cf-current-interval': reportedInterval(currentInterval),
		'data-cf-is-trial': String(isTrial),
		'data-cf-user-role': organization?.Role || DIMENSION_NO_VALUE
	};
}

/**
 * What the organization being created is for: the plan and term the visitor chose before there was an
 * organization to charge, and the arm they were bucketed into when they chose it. Reported there
 * because it is the only place they are known — a trial that never converts completes no purchase for
 * them to be read back from. Every dimension is always reported, so a signup carrying no offer is a value
 * rather than a gap.
 */
export function resolveCreateOrganizationDimensions(returnUrl?: unknown): Record<string, string> {
	const offer = signupOffer(returnUrl);
	const bucketing = readPricingExperimentBucketing();
	const campaign = journeyToken(bucketing?.campaign, MAX_CAMPAIGN_KEY_LENGTH);

	const dimensions: Record<string, string> = {
		'data-cf-target-plan': targetPlan(offer),
		'data-cf-target-interval': reportedInterval(offer.get('interval')),
		'data-cf-arm': DIMENSION_NO_VALUE,
		'data-cf-experiment': DIMENSION_NO_VALUE
	};

	if (bucketing && campaign) {
		dimensions['data-cf-arm'] = bucketing.arm;
		dimensions['data-cf-experiment'] = campaign;
	}

	return dimensions;
}

/**
 * What arriving at the organization-creation page means. Only the signup redirect names itself, so anything
 * else is someone who already had an account and has no organization to go to — one whose only organization
 * was deleted, or who was removed from it.
 */
export function resolveCreateOrganizationStart(entry?: unknown): StageStart {
	return { stage: JourneyStages.CreateOrganization, variant: entry === 'signup' ? 'Signup' : 'OrphanedUser' };
}

export const authProviders: Record<string, string> = {
	Email: 'Email',
	Google: 'Google',
	Facebook: 'Facebook',
	Microsoft: 'Microsoft',
	Custom: 'OpenIdConnect',
	OpenIdConnect: 'OpenIdConnect'
};

export function resolveAuthProvider(provider?: unknown): string | undefined {
	return typeof provider === 'string' && Object.prototype.hasOwnProperty.call(authProviders, provider) ? authProviders[provider] : undefined;
}

/**
 * What each value of the plan page's `source` parameter means as an upgrade intent. The keys are the values
 * actually in use; anything else is treated as no source at all.
 */
const upgradeIntents: Record<string, string> = {
	unavailfeat: 'LimitReached',
	feature: 'FeatureLocked',
	'tryitnow-feature': 'FeatureLocked',
	'upgrade-permissions': 'FeatureLocked',
	'upgrade-folders': 'FeatureLocked',
	orgsettings: 'BillingSettings',
	'upgrade-now': 'UpgradeCTA'
};

const MAX_UPGRADE_DETAIL_LENGTH = 48;

/**
 * Reduces the `details` parameter — the feature code that was gated — to something safe to group by. There
 * are too many feature codes to allowlist, so it is bounded by shape instead.
 */
function upgradeDetail(details: unknown): string | undefined {
	if (typeof details !== 'string')
		return undefined;
	return journeyToken(details, MAX_UPGRADE_DETAIL_LENGTH) || undefined;
}

/**
 * What reaching the plan page means, from the parameters the arrival carries. `Variant` says what kind of
 * pressure brought them and stays a small closed set; the raw `source` goes to `entryPoint` and the feature
 * code to `detail`.
 */
export function resolveUpgradeStart(source?: unknown, details?: unknown, isSignUp = false): StageStart {
	const start: StageStart = {
		stage: JourneyStages.ConvertToPaid,
		variant: isSignUp ? 'Signup' : 'Direct'
	};

	const detail = upgradeDetail(details);
	if (detail)
		start.detail = detail;

	if (typeof source !== 'string' || !Object.prototype.hasOwnProperty.call(upgradeIntents, source))
		return start;

	if (!isSignUp)
		start.variant = upgradeIntents[source];
	start.entryPoint = source;
	return start;
}
